import React, {useRef} from "react";
import {ImagePreview} from "./estimate-tree-detail/EstimateTreeDetailComponent";
import {PhotoEditor} from "./photo-editor/PhotoEditor";
import {PhotoEdit} from "../../shared/services/image-service";

export interface DrawableImageProps {
  /** Null while the Dialog fades out after its page clears the selection. */
  image?: ImagePreview | null,
  onClose: () => void,
  /** Done: save the edit (`saveImageEdit` does both kinds), then close. */
  onSave?: (id: number, edit: PhotoEdit) => void,
  isSaving?: boolean,
  readonly?: boolean
}

/**
 * The photo viewer / editor every page opens a photo in. Since SQ-562 it is
 * the same editor as Android's (photo-editor/PhotoEditor.tsx): marks stay
 * objects, saved with the photo, so either app can move or remove them
 * later. Show it in a Dialog with `fullScreen`, `disableEscapeKeyDown` and
 * `PaperProps={PHOTO_DIALOG_PAPER}`: the editor draws its own window over the
 * dimmed page (full screen on a phone, fitted to the photo elsewhere) and
 * asks before Escape or a click outside discards changes.
 */
/**
 * The Dialog paper for the photo editor: a full-screen frame that dims what is
 * behind it well past MUI's own backdrop, so a window the photo was opened
 * from (the work item's, taller than the photo's) recedes instead of showing
 * its edge and scroll bar above the photo.
 */
export const PHOTO_DIALOG_PAPER = {style: {background: 'rgba(0, 0, 0, 0.6)', boxShadow: 'none'}};

export const DrawableImage = (props: DrawableImageProps) => {
  // The pages close the Dialog by clearing the selected photo, and MUI keeps
  // drawing its children through the fade-out: hold on to the last photo so
  // that frame does not read the id of null.
  const last = useRef<ImagePreview | null>(null);
  if (props.image) last.current = props.image;
  const image = last.current;
  if (!image) return null;
  return (
    <PhotoEditor
      imageId={image.id}
      imageUrl={image.s3Url}
      readonly={props.readonly ?? false}
      isSaving={props.isSaving ?? false}
      onClose={props.onClose}
      onSave={props.onSave ?? (() => {})}
    />
  );
};
